import { normalizeLicensePlate } from './Str';

export function isPresentDeviceInternalId(id: string | number | null | undefined): id is string | number {
    return id != null && id !== '';
}

/** Device fields used to build plate / IMEI → internal_id lookups (e.g. DeviceV3.device). */
export type DeviceInternalIdSource = {
    license_plate: string;
    device_id: string;
    internal_id?: string | number | null;
};

/**
 * Normalized license plate → third-party internal_id from a fleet device list.
 * Omits devices without a usable internal_id. First device wins per normalized plate.
 */
export function buildNormalizedPlateToInternalIdMap(
    devices: Iterable<DeviceInternalIdSource>,
): Map<string, string | number> {
    const byPlate = new Map<string, string | number>();
    for (const dev of devices) {
        const id = dev.internal_id;
        if (!isPresentDeviceInternalId(id)) {
            continue;
        }
        const plateKey = normalizeLicensePlate(dev.license_plate);
        if (!byPlate.has(plateKey)) {
            byPlate.set(plateKey, id);
        }
    }
    return byPlate;
}

/**
 * Plate map (same rules as {@link buildNormalizedPlateToInternalIdMap}) plus IMEI → internal_id
 * for merging internal_id onto API payloads that omit it.
 */
export function buildDeviceInternalIdLookup(
    devices: Iterable<DeviceInternalIdSource>,
): { byPlate: Map<string, string | number>; byImei: Map<string, string | number> } {
    const byImei = new Map<string, string | number>();
    const byPlate = new Map<string, string | number>();
    for (const dev of devices) {
        const id = dev.internal_id;
        if (!isPresentDeviceInternalId(id)) {
            continue;
        }
        byImei.set(dev.device_id, id);
        const plateKey = normalizeLicensePlate(dev.license_plate);
        if (!byPlate.has(plateKey)) {
            byPlate.set(plateKey, id);
        }
    }
    return { byImei, byPlate };
}

export function getInternalIdForNormalizedPlate(
    plateToInternalId: Map<string, string | number>,
    licensePlate: string | undefined | null,
): string | number | undefined {
    return plateToInternalId.get(normalizeLicensePlate(licensePlate ?? undefined));
}
